
import { supabase } from '@/integrations/supabase/client';
import { Integration } from '@/types/integrations';

export const createBaseIntegrationService = (
  setIntegrations: (integrations: Integration[]) => void,
  setIsLoading: (loading: boolean) => void,
  toast: any
) => {
  const fetchIntegrations = async () => {
    try {
      console.log('🔄 baseIntegrationService: Iniciando busca de integrações');
      setIsLoading(true);
      
      const { data, error } = await supabase
        .from('integrations')
        .select('*')
        .order('created_at', { ascending: false });

      console.log('📊 baseIntegrationService: Resposta do Supabase', {
        data: data?.length || 0,
        error: error?.message || null
      });

      if (error) {
        console.error('❌ baseIntegrationService: Erro do Supabase', error);
        throw error;
      }
      
      // Type assertion to ensure integration_type is properly typed
      const typedIntegrations = (data || []).map(item => ({
        ...item,
        integration_type: item.integration_type as 'whatsapp' | 'nuvemshop'
      })) as Integration[];
      
      console.log('✅ baseIntegrationService: Integrações processadas', {
        count: typedIntegrations.length,
        types: typedIntegrations.map(i => i.integration_type)
      });
      
      setIntegrations(typedIntegrations);
    } catch (error: any) {
      console.error('❌ baseIntegrationService: Erro ao carregar integrações', error);
      
      // Não mostrar toast se for erro de rede ou tempo limite
      if (!error.message?.includes('fetch') && !error.message?.includes('timeout')) {
        toast({
          title: "Erro ao carregar integrações",
          description: error.message || "Erro desconhecido",
          variant: "destructive",
        });
      }
      
      // Definir array vazio em caso de erro para evitar crashes
      setIntegrations([]);
    } finally {
      setIsLoading(false);
    }
  };

  const updateIntegration = async (id: string, updates: any) => {
    try {
      console.log('🔄 baseIntegrationService: Atualizando integração', { id, updates });
      
      const { data, error } = await supabase
        .from('integrations')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Integração atualizada",
        description: "Integração atualizada com sucesso.",
      });

      await fetchIntegrations();
      
      // Type assertion for updated integration
      return {
        ...data,
        integration_type: data.integration_type as 'whatsapp' | 'nuvemshop'
      } as Integration;
    } catch (error: any) {
      console.error('❌ baseIntegrationService: Erro ao atualizar integração', error);
      toast({
        title: "Erro ao atualizar integração",
        description: error.message,
        variant: "destructive",
      });
      throw error;
    }
  };

  const deleteIntegration = async (id: string) => {
    try {
      console.log('🔄 baseIntegrationService: Deletando integração', { id });

      // Buscar a integração para verificar o tipo
      const { data: integration, error: fetchError } = await supabase
        .from('integrations')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError) {
        console.error('❌ Erro ao buscar integração:', fetchError);
        throw fetchError;
      }

      // Se for Nuvemshop, limpar webhooks primeiro
      if (integration?.integration_type === 'nuvemshop') {
        console.log('🧹 Limpando webhooks da Nuvemshop antes de deletar...');
        
        try {
          const { data: cleanupResult, error: cleanupError } = await supabase.functions.invoke('nuvemshop-integration', {
            body: { 
              action: 'disconnect_integration',
              integrationId: id
            }
          });

          if (cleanupError) {
            console.error('⚠️ Erro na limpeza de webhooks (continuando):', cleanupError);
            // Não falhar a operação, apenas avisar
            toast({
              title: "Aviso",
              description: "Houve um problema ao limpar webhooks, mas a integração será removida.",
              variant: "default",
            });
          } else {
            console.log('✅ Webhooks limpos:', cleanupResult);
          }
        } catch (cleanupError) {
          console.error('⚠️ Erro na limpeza de webhooks (continuando):', cleanupError);
          // Não falhar a operação, apenas avisar
        }
      }
      
      // Deletar a integração
      const { error } = await supabase
        .from('integrations')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Integração removida",
        description: "Integração e webhooks removidos com sucesso.",
      });

      await fetchIntegrations();
    } catch (error: any) {
      console.error('❌ baseIntegrationService: Erro ao remover integração', error);
      toast({
        title: "Erro ao remover integração",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  return {
    fetchIntegrations,
    updateIntegration,
    deleteIntegration
  };
};
